import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import GameCover from '../tavern/GameCover';
import {
  gameMetaLine,
  gameTitle,
} from './gameFormat';
import {
  localizedDescription,
  localizedList,
  localizedPrimaryTags,
} from './bggLocalization';

const Field = ({ fallback, label, value }) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary">
      {label}
    </Typography>
    <Typography>{value || fallback}</Typography>
  </Box>
);

const GameDetailsDialog = ({ game, open, onClose, onEdit, onDelete }) => {
  const { i18n, t } = useTranslation();
  if (!game) return null;
  const language = i18n.resolvedLanguage || i18n.language;
  const description = localizedDescription(game, language, t);
  const tags = localizedPrimaryTags(game, language, 8);
  const hasActions = Boolean(onDelete || onEdit);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          pr: 1.5,
        }}
      >
        <Box component="span" sx={{ minWidth: 0 }}>
          {gameTitle(game, t, language)}
        </Box>
        <IconButton
          aria-label={t('common.close')}
          edge="end"
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            flex: '0 0 auto',
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Box sx={{ width: { xs: '100%', md: 180 }, flex: '0 0 auto' }}>
            <GameCover game={game} sx={{ width: '100%' }} />
          </Box>
          <Stack spacing={2.25} sx={{ minWidth: 0 }}>
            <Typography color="text.secondary">{gameMetaLine(game, t)}</Typography>
            <Box>
              <Typography>{description.text}</Typography>
              {description.isFallback && (
                <Chip
                  label={t('games.details.descriptionFallback')}
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" />
              ))}
              {tags.length === 0 && (
                <Chip label={t('games.details.noTags')} size="small" variant="outlined" />
              )}
            </Stack>
            <Divider />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2,
              }}
            >
              <Field fallback={t('common.unknown')} label={t('games.details.published')} value={game.yearPublished} />
              <Field fallback={t('common.unknown')} label={t('games.details.minimumAge')} value={game.minAge ? `${game.minAge}+` : null} />
              <Field fallback={t('common.unknown')} label={t('games.details.bggScore')} value={game.bggAverage} />
              <Field fallback={t('common.unknown')} label={t('games.details.source')} value={game.source === 'bgg' ? 'BoardGameGeek' : t('games.details.manualSource')} />
              <Field fallback={t('common.unknown')} label={t('games.details.designers')} value={localizedList(game.designers, language)} />
              <Field fallback={t('common.unknown')} label={t('games.details.publishers')} value={localizedList(game.publishers, language)} />
            </Box>
            {game.notes && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('games.details.yourNote')}
                </Typography>
                <Typography>{game.notes}</Typography>
              </Box>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      {hasActions && (
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          {onDelete && (
            <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => onDelete(game)}>
              {t('common.remove')}
            </Button>
          )}
          {onEdit && (
            <Button startIcon={<EditIcon />} onClick={() => onEdit(game)}>
              {t('common.edit')}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default GameDetailsDialog;
